import React, { useEffect, useRef, useState } from "react";
import DeckGL from "@deck.gl/react/typed";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  BitmapLayer,
  GeoJsonLayer,
  GridCellLayer,
  IconLayer,
  PathLayer,
} from "@deck.gl/layers/typed";
import {
  StaticMap,
  MapContext,
  NavigationControl,
  Marker,
  MapRef,
  ViewState,
} from "react-map-gl";
import { Box, Fab, Typography, useTheme } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { TileLayer } from "@deck.gl/geo-layers/typed";

import {
  selectAircraftVitals,
  selectLive,
} from "../../features/aircraft/aircraftSelectors";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import {
  cellData,
  getUavIcon,
  uavImageColours,
  uavOtherImageColours,
  uavStateColour,
} from "../../features/aircraft/state";
import {
  selectGeofences,
  selectLandingZone,
  selectPads,
} from "../../features/airspace/airspaceSlice";
import { selectCell, selectRegion } from "../../features/session/sessionSlice";
import { ReactComponent as Navigation } from "../../assets/svg/Navigation.svg";
import { getLandingZoneListThunk } from "../../features/airspace/airspaceSlice";
import { Clock } from "../../components/Clock";

// ADSB Traffic imports
import {
  AircraftAlert,
  SseConnectionStatus,
  TrailPoint,
  getAlertLevelColor,
} from "../../features/adsb/adsbTypes";
import { connectAdsbAlertsSse } from "../../features/adsb/adsbAPI";
import useAdsbAlerts from "../../hooks/useAdsbAlerts";
import useOffScreenAdsbAlerts from "../../hooks/useOffScreenAdsbAlerts";
import { hexToRgb } from "../../utils/color";

const TRAIL_DURATION_MS = 30000;
const TRACK_PROJECTION_SECONDS = 60;
const STALE_TRAFFIC_MS = 60000;
const FEET_TO_METERS = 0.3048;
const SSE_RECONNECT_INITIAL_MS = 2000;
const SSE_RECONNECT_MAX_MS = 30000;
const SSE_HEARTBEAT_TIMEOUT_MS = 30000;

const MAP_STYLE = "mapbox://styles/mapbox/dark-v9";

interface MapboxMapProps {
  showTiles: boolean;
  showTraffic: boolean;
  onSseStatusChange?: (status: SseConnectionStatus) => void;
}

export const MapboxMap = (props: MapboxMapProps) => {
  const { showTiles, showTraffic, onSseStatusChange } = props;

  const live = useAppSelector(selectAircraftVitals);
  const relay = useAppSelector(selectLive);
  const geofences = useAppSelector(selectGeofences);
  const [mapRef, setMapRef] = useState<MapRef | null>(null);
  const pads = useAppSelector(selectPads);
  const theme = useTheme();
  const cell = useAppSelector(selectCell);
  const landingZones = useAppSelector(selectLandingZone);
  const [hoverInfo, setHoverInfo] = useState<any>();
  const fabRef = React.useRef<HTMLButtonElement | null>(null);

  // ADSB Traffic state
  const [aircraftAlerts, setAircraftAlerts] = useState<AircraftAlert[]>([]);
  const [trails, setTrails] = useState<Record<string, TrailPoint[]>>({});
  const [now, setNow] = useState(() => Date.now());
  const [sseConnectionStatus, setSseConnectionStatus] = useState<SseConnectionStatus>('disconnected');
  // Tracks the last time (client clock) each aircraft (by hex) was updated,
  // used to drop traffic that hasn't reported in for STALE_TRAFFIC_MS.
  const lastSeenRef = useRef<Record<string, number>>({});

  const dispatch = useAppDispatch();

  const adsbLayers = useAdsbAlerts(
    aircraftAlerts,
    showTraffic,
    trails,
    now,
    TRAIL_DURATION_MS,
    TRACK_PROJECTION_SECONDS
  );
  const offScreenIndicators = useOffScreenAdsbAlerts(aircraftAlerts, mapRef, showTraffic);

  // Tick clock for breadcrumb fade animation and to drop stale traffic
  // that hasn't reported an update within STALE_TRAFFIC_MS.
  useEffect(() => {
    if (!showTraffic) return;

    const interval = setInterval(() => {
      const currentTime = Date.now();
      setNow(currentTime);

      const isStale = (hex: string) => {
        const seen = lastSeenRef.current[hex];
        return seen === undefined || currentTime - seen > STALE_TRAFFIC_MS;
      };

      setAircraftAlerts((prev) => prev.filter((a) => !isStale(a.hex)));
      setTrails((prev) => {
        const staleHexes = Object.keys(prev).filter(isStale);
        if (staleHexes.length === 0) return prev;
        const next = { ...prev };
        staleHexes.forEach((hex) => {
          delete next[hex];
          delete lastSeenRef.current[hex];
        });
        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showTraffic]);

  useEffect(() => {
    onSseStatusChange?.(sseConnectionStatus);
  }, [sseConnectionStatus, onSseStatusChange]);

  // SSE connection for ADS-B alerts when cell changes (or traffic display is toggled)
  useEffect(() => {
    if (!showTraffic) {
      setSseConnectionStatus('disconnected');
      return;
    }

    if (!cell?.id) {
      console.log('[ADSB] No cell selected, skipping SSE connection');
      setAircraftAlerts([]);
      setTrails({});
      lastSeenRef.current = {};
      setSseConnectionStatus('disconnected');
      return;
    }

    console.log(`[ADSB] Attempting SSE connection for cell ${cell.id} (${cell.cellName})`);
    setAircraftAlerts([]);
    setTrails({});
    lastSeenRef.current = {};
    setSseConnectionStatus('connecting');

    let eventSource: EventSource | null = null;
    let heartbeatTimeout: ReturnType<typeof setTimeout> | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    // Guards against the connection promise resolving after this effect has
    // been cleaned up (e.g. cell/showTraffic changed or component unmounted).
    let cancelled = false;
    let abortController: AbortController | null = null;
    let retryDelay = SSE_RECONNECT_INITIAL_MS;

    const clearHeartbeatTimeout = () => {
      if (heartbeatTimeout) {
        clearTimeout(heartbeatTimeout);
        heartbeatTimeout = null;
      }
    };

    const closeEventSource = () => {
      if (eventSource) {
        eventSource.close();
        eventSource = null;
      }
    };

    const resetBackoff = () => {
      retryDelay = SSE_RECONNECT_INITIAL_MS;
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      closeEventSource();
      abortController?.abort();
      abortController = null;
      clearHeartbeatTimeout();
      setSseConnectionStatus('error');
      reconnectTimeout = setTimeout(() => {
        reconnectTimeout = null;
        if (cancelled) return;
        attemptConnection();
      }, retryDelay);
      retryDelay = Math.min(retryDelay * 2, SSE_RECONNECT_MAX_MS);
    };

    const resetHeartbeatTimeout = () => {
      clearHeartbeatTimeout();
      heartbeatTimeout = setTimeout(() => {
        if (cancelled) return;
        console.log('[ADSB] Heartbeat timeout — no data received in 30s');
        scheduleReconnect();
      }, SSE_HEARTBEAT_TIMEOUT_MS);
    };

    const attemptConnection = () => {
      if (cancelled) return;

      closeEventSource();
      abortController?.abort();
      abortController = new AbortController();
      clearHeartbeatTimeout();
      setSseConnectionStatus('connecting');

      connectAdsbAlertsSse(cell.id.toString(), abortController.signal)
        .then((es) => {
          if (cancelled) {
            es.close();
            return;
          }
          eventSource = es;
          console.log('[ADSB] SSE connection established');
          resetBackoff();
          setSseConnectionStatus('connected');
          resetHeartbeatTimeout();

          es.addEventListener('alert', (event: any) => {
            if (cancelled) return;
            setSseConnectionStatus('connected');
            resetBackoff();
            resetHeartbeatTimeout();
            try {
              const data = JSON.parse(event.data);
              const telemetry = data.telemetry;
              const hasValidCoordinates =
                Number.isFinite(telemetry?.lat) && Number.isFinite(telemetry?.lon);

              if (telemetry?.valid && hasValidCoordinates) {
                const aircraft: AircraftAlert = {
                  hex: telemetry.hex,
                  flight: telemetry.flight || 'N/A',
                  registration: telemetry.r || 'N/A',
                  type: telemetry.t || 'Unknown',
                  altitude: (telemetry.alt_geom ?? 0) * FEET_TO_METERS,
                  groundSpeed: telemetry.gs || 0,
                  track: telemetry.track || 0,
                  lat: telemetry.lat,
                  lon: telemetry.lon,
                  timestamp: data.timestamp,
                  alertLevel: data.alert?.level || 'UNKNOWN',
                  alertMessage: data.alert?.message || '',
                  distance: telemetry.dst,
                  baroRate: telemetry.baro_rate,
                  geomRate: telemetry.geom_rate,
                };

                setAircraftAlerts((prev) => {
                  const filtered = prev.filter((a) => a.hex !== aircraft.hex);
                  return [...filtered, aircraft];
                });

                const receivedAt = Date.now();
                lastSeenRef.current[aircraft.hex] = receivedAt;
                setTrails((prev) => {
                  const existing = prev[aircraft.hex] || [];
                  const updated = [
                    ...existing,
                    { lat: aircraft.lat, lon: aircraft.lon, timestamp: receivedAt },
                  ].filter((p) => receivedAt - p.timestamp <= TRAIL_DURATION_MS);
                  return { ...prev, [aircraft.hex]: updated };
                });
              }
            } catch (error) {
              console.error('[ADSB] Error parsing alert data:', error);
            }
          });

          es.addEventListener('heartbeat', () => {
            if (cancelled) return;
            setSseConnectionStatus('connected');
            resetBackoff();
            resetHeartbeatTimeout();
          });

          es.onerror = () => {
            if (cancelled) return;
            console.error('[ADSB] SSE connection error');
            if (es.readyState === EventSource.CLOSED) {
              console.log('[ADSB] SSE connection closed');
              scheduleReconnect();
            } else if (es.readyState === EventSource.CONNECTING) {
              console.log('[ADSB] SSE reconnecting...');
              setSseConnectionStatus('connecting');
            }
          };
        })
        .catch((error) => {
          if (cancelled) return;
          if (error.message === 'ADS-B SSE connection cancelled') return;
          console.error('[ADSB] Failed to connect to ADS-B alerts:', error.message);
          scheduleReconnect();
        });
    };

    attemptConnection();

    return () => {
      cancelled = true;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      abortController?.abort();
      clearHeartbeatTimeout();
      closeEventSource();
      setSseConnectionStatus('disconnected');
    };
  }, [cell?.id, showTraffic]);

  useEffect(() => {
    if (mapRef) {
      const map = mapRef.getMap();
      map.dragRotate.disable();
      map.touchZoomRotate.disableRotation();
    }
  }, [mapRef]);

  const displayAltitude = (alt: number | null | undefined) => {
    if (!alt) return "0";
    if (alt < 0) return "0";
    return alt.toFixed(0);
  };

  const [initialViewState, setInitialViewState] = useState<ViewState>({
    latitude:
      cell?.centerLatitude || cellData[cell?.cellName || ""]?.latLon?.[0],
    longitude:
      cell?.centerLongitude || cellData[cell?.cellName || ""]?.latLon?.[1],
    zoom: 11,
    bearing: 0,
    pitch: 0,
  });

  useEffect(() => {
    const loc = cellData[cell?.cellName || ""];
    setInitialViewState({
      latitude: cell?.centerLatitude || loc?.latLon?.[0] || 0,
      longitude: cell?.centerLongitude || loc?.latLon?.[1] || 0,
      zoom: 14,
      pitch: 0,
      bearing: 0,
    });
  }, [cell]);

  useEffect(() => {
    console.log("Cell changed, fetching landing zones");
    if (cell) {
      dispatch(getLandingZoneListThunk({}));
    }
  }, [cell, dispatch]);

  const onClickCentre = () => {
    const loc = cellData[cell?.cellName || ""];
    setInitialViewState({
      latitude: cell?.centerLatitude || loc?.latLon?.[0] || 0,
      longitude: cell?.centerLongitude || loc?.latLon?.[1] || 0,
      zoom: 14,
      pitch: 0,
      bearing: 0,
      // @ts-ignore
      transitionDuration: Math.random(),
      // workaround, for some reason the transitionDuration needs to change every time to work.
    });
  };

  const onClickFab = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onClickCentre();
  };

  useEffect(() => {
    const handleTouch = (e: TouchEvent) => {
      e.preventDefault();
      onClickCentre();
    };

    const element = fabRef.current;
    element?.addEventListener("touchstart", handleTouch, { passive: false });

    return () => {
      element?.removeEventListener("touchstart", handleTouch);
    };
  }, []);
  return (
    <DeckGL
      initialViewState={{ ...initialViewState, minZoom: 12, maxZoom: 20 }}
      controller={{
        touchRotate: false,
        dragRotate: false,
        keyboard: {
          rotateSpeedX: 0,
          rotateSpeedY: 0,
        },
      }}
      layers={[
        showTiles
          ? new TileLayer({
              id: "satellite-tiles2",
              data: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
              minZoom: 0,
              maxZoom: 19,
              tileSize: 256,
              renderSubLayers: (props: any) => {
                const {
                  bbox: { west, south, east, north },
                } = props.tile;
                return new BitmapLayer(props, {
                  // @ts-ignore
                  data: null,
                  image: props.data,
                  bounds: [west, south, east, north],
                });
              },
            })
          : null,
        showTiles
          ? new TileLayer({
              id: "satellite-tiles",
              data: "https://tiles.manna.aero/{z}/{x}/{y}.png",
              minZoom: 12,
              maxZoom: 22,
              tileSize: 256,
              renderSubLayers: (props: any) => {
                const {
                  bbox: { west, south, east, north },
                } = props.tile;
                return new BitmapLayer(props, {
                  // @ts-ignore
                  data: null,
                  image: props.data,
                  bounds: [west, south, east, north],
                });
              },
            })
          : null,
        geofences.map((fence) => {
          return new GeoJsonLayer({
            id: "path-layer-fences" + fence.id,
            data: fence.geoJson,
            getWidth: 2,
            widthUnits: "pixels",
            pickable: true,
            lineWidthScale: 0.5,
            lineWidthMinPixels: 0.5,
            getLineColor: [255, 255, 255],
            filled: false,
            getPointRadius: 0.5,
            getLineWidth: 8,
            opacity: 1,
          });
        }),
        landingZones.map((fence) => {
          return new GeoJsonLayer({
            id: "path-layer-fences" + fence.id,
            data: fence.geoJson,
            getWidth: 2,
            widthUnits: "pixels",
            pickable: true,
            lineWidthScale: 0.5,
            lineWidthMinPixels: 0.5,
            getLineColor: [255, 255, 255],
            filled: false,
            getPointRadius: 0.5,
            getLineWidth: 8,
            opacity: 1,
          });
        }),

        new GridCellLayer({
          id: "grid-cell-layer",
          data: pads,
          pickable: true,
          extruded: true,
          cellSize: 10,
          elevationScale: 10,
          getPosition: (d) => [d.longitude, d.latitude],
          getFillColor: (d) => [255, 255, 255],
          getElevation: 0,
          offset: [-0, -0],
          onHover: (info) => setHoverInfo(info),
        }),
        live.map((aircraft) => {
          if (!aircraft || !aircraft.mission?.geoJson) return null;
          const inEmergency = aircraft.uavSubState?.includes("STATE_FLIGHT_EOPS") ?? false;
          const colour = uavStateColour(
            aircraft.uavState || "",
            aircraft.flightMode || "",
            false,
            false,
            inEmergency
          );
          return new GeoJsonLayer({
            id: "path-layer" + aircraft.uavId,
            data: aircraft.mission?.geoJson,
            getWidth: 2,
            widthUnits: "pixels",
            pickable: true,
            lineWidthScale: 1,
            lineWidthMinPixels: 1,
            getLineColor: hexToRgb(colour.route),
            getPointRadius: 100,
            getLineWidth: 1,
            getElevation: 2000,
            lineCapRounded: true,
          });
        }),

        new PathLayer({
          id: "path-layer-2",
          data: Object.values(relay).map((aircraft) => {
            if (!aircraft || !aircraft.mission?.geoJson) return null;
            const inEmergency = aircraft.uavSubState?.includes("STATE_FLIGHT_EOPS") ?? false;
            const colour = uavStateColour(
              aircraft.uavState || "",
              aircraft.flightMode || "",
              false,
              false,
              inEmergency
            );

            return {
              path:
                aircraft.mission?.geoJson?.features.map((f) => {
                  return f?.geometry?.coordinates;
                }) || [],
              color: hexToRgb(colour.route),
            };
          }),
          getPath: (d) => d?.path,
          getWidth: 2,
          widthUnits: "pixels",
          pickable: true,
          lineWidthScale: 1,
          lineWidthMinPixels: 1,
          getColor: (d) => d?.color,

          getPointRadius: 100,
          getLineWidth: 1,
          getElevation: 2000,
        }),
        new IconLayer({
          id: "icon-layer",
          data: live.map((aircraft) => {
            if (!aircraft) return null;
            const inEmergency = aircraft.uavSubState?.includes("STATE_FLIGHT_EOPS") ?? false;
            const icon =
              uavImageColours[
                getUavIcon(aircraft.uavState || "", aircraft.flightMode || "", inEmergency)
              ];
            const icon2 =
              uavOtherImageColours[
                getUavIcon(aircraft.uavState || "", aircraft.flightMode || "", inEmergency)
              ];
            return {
              coordinates: [aircraft.lon, aircraft.lat],
              heading: aircraft.heading,
              size: 64,
              controlled: aircraft?.controlled,
              avatar_url: aircraft?.controlled ? icon : icon2,
            };
          }),
          getIcon: (d) => ({
            url: d.avatar_url,
            width: d.size,
            height: d.size,
          }),
          getSize: (d) => (d?.controlled ? 1.5 : 0.8),
          pickable: false,
          visible: true,
          sizeScale: 24,

          getPosition: (d) => d.coordinates,
          getAngle: (d) => -d.heading,
          getElevation: 5000,
        }),
        // ADS-B Traffic layers
        ...adsbLayers,
      ]}
      // @ts-ignore
      ContextProvider={MapContext.Provider}
    >
      <StaticMap
        ref={setMapRef}
        mapStyle={MAP_STYLE}
        mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
      />
      <NavigationControl
        style={{ position: "absolute", top: 10, left: 10 }}
        showCompass={false}
      />
      <Box style={{ position: "absolute", top: 10, left: 50 }}>
        <Clock />
        <Typography
          variant={"h4"}
          fontSize={15}
          fontWeight={600}
          textAlign={"start"}
        >
          {new Date().toLocaleDateString('en-US', { 
            weekday: 'short', 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
          })}
        </Typography>
        <Typography
          variant={"h4"}
          fontSize={15}
          fontWeight={600}
          textAlign={"start"}
        >
          v{process.env.REACT_APP_BASE_HASH || ""}
        </Typography>
        <Typography
          variant={"h4"}
          fontSize={15}
          fontWeight={600}
          textAlign={"start"}
        >
          {cell?.cellName || ""}
        </Typography>
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: 0,
          top: 72,
          borderRadius: 0,
          bgcolor: "transparent",
          zIndex: 500,
          pointerEvents: "none",
        }}
      >
        <Box sx={{ pointerEvents: "none" }}>
          <Fab
            sx={{
              top: 16,
              right: 16,
              pointerEvents: "all",
              backgroundColor: theme.palette.background.paper,
            }}
            color={"default"}
            ref={fabRef}
            onClick={onClickFab}
          >
            <Navigation stroke={theme.palette.text.primary} />
          </Fab>
        </Box>
      </Box>
      {live.map((aircraft) => {
        if (!aircraft) return null;
        return (
          <Marker
            key={`marker-${aircraft.uavId}`}
            longitude={aircraft.lon || 0}
            latitude={aircraft.lat || 0}
            offsetTop={16}
            offsetLeft={-52}
            style={{ zIndex: 0 }}
          >
            <Box
              display={"flex"}
              sx={{
                flexDirection: "column",
                borderRadius: 2,
                width: 101,
                boxSizing: "border-box",
              }}
            >
              <Box
                display={"flex"}
                flex={1}
                sx={{}}
                alignItems="center"
                justifyItems="center"
              >
                <Typography
                  color={"white"}
                  sx={{
                    width: "100%",
                    textJustify: "center",
                    textAlign: "center",
                    fontSize: 14,
                    textShadow: "black 2px 5px 4px",
                    fontWeight: 900,
                  }}
                >{`${aircraft.uavId}`}</Typography>
              </Box>

              <Box
                display={"flex"}
                flex={1}
                sx={{}}
                alignItems="center"
                justifyItems="center"
              >
                <Typography
                  sx={{
                    width: "100%",
                    textJustify: "center",
                    textAlign: "center",
                    textShadow: "black 2px 5px 4px",

                    fontSize: 14,
                    fontWeight: 900,
                  }}
                  color={"white"}
                >{`${displayAltitude(aircraft.altitude)}m`}</Typography>
              </Box>
            </Box>
          </Marker>
        );
      })}
      {hoverInfo && (
        <Box
          style={{
            position: "absolute",
            zIndex: 1,
            pointerEvents: "none",
            left: hoverInfo?.x + 8,
            top: hoverInfo?.y + 12,
          }}
        >
          <Typography
            variant="h6"
            fontWeight={800}
            sx={{ textShadow: "black 2px 5px 4px" }}
          >
            {hoverInfo?.object?.name}
          </Typography>
        </Box>
      )}
      {showTraffic &&
        offScreenIndicators.map((indicator) => {
          const color = getAlertLevelColor(indicator.alertLevel);
          const distanceLabel =
            indicator.distanceKm < 10
              ? `${indicator.distanceKm.toFixed(1)}km`
              : `${Math.round(indicator.distanceKm)}km`;

          return (
            <Box
              key={`offscreen-${indicator.hex}`}
              style={{
                position: "absolute",
                left: indicator.x,
                top: indicator.y,
                transform: "translate(-50%, -50%)",
                pointerEvents: "none",
                zIndex: 400,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <ArrowUpwardIcon
                style={{
                  color,
                  transform: `rotate(${indicator.angleDeg}deg)`,
                  fontSize: 28,
                  filter: "drop-shadow(0px 0px 3px rgba(0,0,0,0.9))",
                }}
              />
              <Typography
                style={{
                  color,
                  fontSize: 11,
                  fontWeight: 700,
                  textShadow: "black 1px 1px 3px",
                  marginTop: -2,
                }}
              >
                {distanceLabel}
              </Typography>
            </Box>
          );
        })}
    </DeckGL>
  );
};
